import { useCallback, useEffect, useRef, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { EmojiListProps } from "./types"

export default function EmojiList({
	items,
	command,
	setOnKeyDownHandler,
}: EmojiListProps) {
	const [selected, setSelected] = useState(0)
	const selectedRef = useRef(0)

	const moveSelection = useCallback((index: number) => {
		selectedRef.current = index
		setSelected(index)
	}, [])

	useEffect(() => moveSelection(0), [items, moveSelection])

	const select = useCallback(
		(index: number) => {
			const item = items[index]
			if (!item) return
			command({ emoji: item.emoji })
		},
		[command, items],
	)

	useEffect(() => {
		setOnKeyDownHandler(({ event }) => {
			if (items.length === 0) return false

			switch (event.key) {
				case "ArrowUp":
					moveSelection((selectedRef.current + items.length - 1) % items.length)
					return true
				case "ArrowDown":
					moveSelection((selectedRef.current + 1) % items.length)
					return true
				case "Enter":
				case "Tab":
					select(selectedRef.current)
					return true
				default:
					return false
			}
		})
	}, [items, moveSelection, select, setOnKeyDownHandler])

	if (items.length === 0) return null

	return (
		<div className="min-w-56 rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-900">
			<ul className="m-0 flex list-none flex-col p-0">
				{items.map((item, index) => (
					<li key={item.shortcode}>
						<button
							onClick={() => select(index)}
							onMouseMove={() => moveSelection(index)}
							className={cn(
								"flex max-h-8 min-h-8 w-full items-center gap-2.5 rounded-md px-2 py-1 text-left",
								selected === index && "bg-gray-100 dark:bg-gray-700/50",
							)}
						>
							<span className="text-base leading-none">{item.emoji}</span>
							<span className="truncate text-sm">{item.shortcode}</span>
						</button>
					</li>
				))}
			</ul>
		</div>
	)
}
